import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { 
  FaFileInvoice, FaWallet, FaReceipt, FaChartBar, FaUpload, 
  FaBookOpen, FaSlidersH, FaCoins, FaMoneyBillWave, FaPercentage, 
  FaExclamationTriangle, FaCheckCircle, FaChevronRight, FaPlus
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number | string;
  company_name: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  HSN_SAC?: string;
  GST_SLAB?: string;
  [key: string]: any;
}

interface Customer {
  id: number | string;
  company_name: string;
  city?: string;
  state?: string;
  gst_number?: string;
  gst_uin?: string;
  [key: string]: any;
}

interface InvoiceFromAPI {
  _id?: string;
  Company_id: string | number;
  Customer_id: string | number;
  InvoiceNo: string;
  Date: string;
  Total_Amount: number;
  PaymentStatus?: "Paid" | "Unpaid" | "Partially Paid" | string;
  AmountPaid?: number;
  [key: string]: any;
}

const STATUS_OPTIONS = [
  { label: "All Statuses", value: "" },
  { label: "Paid", value: "Paid" },
  { label: "Unpaid", value: "Unpaid" },
  { label: "Partially Paid", value: "Partially Paid" }
];

function formatDate(date: string) {
  try {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return date || "";
  }
}

function isInvoiceInSelectedFys(inv: any, selectedFys: string[]): boolean {
  if (!selectedFys || selectedFys.length === 0) return true;
  if (inv.financial_year) {
    return selectedFys.includes(inv.financial_year);
  }
  const d = new Date(inv.Date);
  if (isNaN(d.getTime())) return false;
  const year = d.getFullYear();
  const month = d.getMonth();
  let fy = "";
  if (month >= 3) {
    fy = `${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
  } else {
    fy = `${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
  }
  return selectedFys.includes(fy);
}

function getCompanyName(companies: Company[], id: string | number) {
  return companies.find((c) => String(c.id) === String(id))?.company_name || "";
}

function getCustomerName(customers: Customer[], id: string | number) {
  return customers.find((c) => String(c.id) === String(id))?.company_name || "";
}

function currency(n: number | string) {
  let num = Number(n) || 0;
  return "₹ " + num.toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

function getStatusBadge(paymentStatus: number | null) {
  if (paymentStatus === 1)
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-bold border border-green-200">
        Paid
      </span>
    );
  if (paymentStatus === 2)
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold border border-blue-200">
        Partial
      </span>
    );
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold border border-red-200">
      Unpaid
    </span>
  );
}

function getPaidPartial(inv: InvoiceFromAPI) {
  if (inv.paid === 1) return currency(inv.Total_Amount);
  if (inv.paid === 2) {
    const paidAmt = inv.amount_paid !== undefined ? inv.amount_paid : (inv.AmountPaid || 0);
    return (
      <div className="flex flex-col">
        <span className="text-green-700 font-bold">{currency(paidAmt)}</span>
        <span className="text-[10px] text-gray-400">of {currency(inv.Total_Amount)}</span>
      </div>
    );
  }
  return (
    <span className="text-gray-400 font-medium">
      {currency(0)} <span className="text-[10px]">of {currency(inv.Total_Amount)}</span>
    </span>
  );
}

function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

const Dashboard: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [invoices, setInvoices] = useState<InvoiceFromAPI[]>([]);
  const [purchases, setPurchases] = useState<any[]>([]);
  const [bankRows, setBankRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string>("");

  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchInvoiceNo, setSearchInvoiceNo] = useState<string>("");

  const [fyFilter, setFyFilter] = useState<string>("");
  const [monthFilter, setMonthFilter] = useState<string>("");
  const [quarterFilter, setQuarterFilter] = useState<string>("");
  const [halfYearFilter, setHalfYearFilter] = useState<string>("");

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        let token = localStorage.getItem("token");
        let decoded: any = token ? decodeToken(token) : {};
        const localRole = decoded.role;
        const [cRes, cuRes, iRes, pRes, setRes, bRes] = await Promise.all([
          axios.get(`${API_URL}/auth/companies`),
          axios.get(`${API_URL}/auth/customers`),
          axios.get(`${API_URL}/auth/invoices`),
          axios.get(`${API_URL}/auth/purchase-invoices`),
          axios.get(`${API_URL}/auth/setting`),
          axios.get(`${API_URL}/auth/bank-statement-rows`).catch(() => ({ data: [] }))
        ]);
        
        const companiesData = cRes.data || [];
        const customersData = cuRes.data || [];
        let invoicesData = iRes.data || [];
        let purchasesData = pRes.data || [];
        const settingData = setRes.data || {};
        const bankData = bRes.data || [];

        let selectedFys: string[] = [];
        try {
          if (settingData.selected_fys) {
            selectedFys = JSON.parse(settingData.selected_fys);
          }
        } catch (e) {}

        if (selectedFys.length > 0) {
          invoicesData = invoicesData.filter((inv: any) => isInvoiceInSelectedFys(inv, selectedFys));
          purchasesData = purchasesData.filter((p: any) => isInvoiceInSelectedFys(p, selectedFys));
        }

        setCompanies(companiesData);
        setCustomers(customersData);
        setBankRows(bankData);

        if (localRole === "company" && decoded && decoded.userId) {
          invoicesData = invoicesData.filter(
            (inv: any) => String(inv.Company_id) === String(decoded.userId)
          );
          purchasesData = purchasesData.filter(
            (p: any) => String(p.Company_id) === String(decoded.userId)
          );
        }
        setInvoices(invoicesData);
        setPurchases(purchasesData);
        setFetchError("");
      } catch (err) {
        setFetchError("Could not fetch dashboard data.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const getFinancialYear = (dateStr: string): string => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = d.getMonth();
    if (month >= 3) {
      return `${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
    } else {
      return `${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
    }
  };

  const parseBankDate = (dateStr: string): string => {
    if (!dateStr) return "";
    const clean = dateStr.trim();
    const parts = clean.split(/[-/\s]+/);
    if (parts.length === 3) {
      const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
      let monthVal = NaN;
      if (/^\d+$/.test(parts[1])) {
        monthVal = Number(parts[1]);
      } else {
        monthVal = months.indexOf(parts[1].toLowerCase()) + 1;
      }
      let year = 2026, day = 1;
      if (parts[0].length === 4) {
        year = Number(parts[0]);
        day = Number(parts[2]);
      } else {
        year = Number(parts[2]);
        day = Number(parts[0]);
      }
      if (!isNaN(year) && monthVal > 0 && monthVal <= 12 && !isNaN(day)) {
        return `${year}-${String(monthVal).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
    return dateStr;
  };

  const matchesPeriodFilters = useCallback((dateStr: string) => {
    if (!dateStr) return true;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return true;

    if (fyFilter) {
      const fy = getFinancialYear(dateStr);
      if (fy !== fyFilter) return false;
    }

    if (monthFilter) {
      const month = String(d.getMonth() + 1).padStart(2, '0');
      if (month !== monthFilter) return false;
    }

    if (quarterFilter) {
      const m = d.getMonth();
      let q = "";
      if (m >= 3 && m <= 5) q = "Q1";
      else if (m >= 6 && m <= 8) q = "Q2";
      else if (m >= 9 && m <= 11) q = "Q3";
      else if (m >= 0 && m <= 2) q = "Q4";
      if (q !== quarterFilter) return false;
    }

    if (halfYearFilter) {
      const m = d.getMonth();
      let hy = "";
      if (m >= 3 && m <= 8) hy = "H1";
      else hy = "H2";
      if (hy !== halfYearFilter) return false;
    }

    return true;
  }, [fyFilter, monthFilter, quarterFilter, halfYearFilter]);

  const availableFys = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach(i => {
      const fy = getFinancialYear(i.Date);
      if (fy) set.add(fy);
    });
    purchases.forEach(p => {
      const fy = getFinancialYear(p.Date);
      if (fy) set.add(fy);
    });
    return Array.from(set).sort().reverse();
  }, [invoices, purchases]);

  const companyFilteredInvoices = useMemo(() => {
    if (!companyFilter) return invoices;
    return invoices.filter((inv) => String(inv.Company_id) === companyFilter);
  }, [invoices, companyFilter]);

  const summary = useMemo(() => {
    const periodFilteredInvoices = companyFilteredInvoices.filter(inv => matchesPeriodFilters(inv.Date));

    let total = periodFilteredInvoices.length;
    let paid = 0,
      unpaid = 0,
      partial = 0,
      paidAmount = 0,
      unpaidAmount = 0,
      partialAmount = 0,
      totalGst = 0,
      totalTdsReceivable = 0;

    periodFilteredInvoices.forEach((inv) => {
      if (inv.cancelled === 1) return;
      totalGst += (Number(inv.SGSTAmount) || 0) + (Number(inv.CGSTAmount) || 0) + (Number(inv.IGSTAmount) || 0);
      totalTdsReceivable += Number(inv.TDSAmount) || 0;

      if (inv.paid === 1) {
        paid++;
        paidAmount += inv.Total_Amount;
      } else if (inv.paid === 2) {
        partial++;
        const paidPart = Number(inv.amount_paid !== undefined ? inv.amount_paid : inv.AmountPaid) || 0;
        paidAmount += paidPart;
        partialAmount += inv.Total_Amount - paidPart;
      } else {
        unpaid++;
        unpaidAmount += inv.Total_Amount;
      }
    });

    const companyFilteredPurchases = companyFilter
      ? purchases.filter((p) => String(p.Company_id) === companyFilter)
      : purchases;
    const periodFilteredPurchases = companyFilteredPurchases.filter(p => matchesPeriodFilters(p.Date));

    // Incorporate direct expense data from reconciled bank statement rows
    const companyFilteredBankRows = companyFilter
      ? bankRows.filter(r => String(r.company_id) === companyFilter)
      : bankRows;
    const periodFilteredBankRows = companyFilteredBankRows.filter(r => {
      const parsedDate = parseBankDate(r.date_str);
      return matchesPeriodFilters(parsedDate);
    });

    // Sum TDS and GST from direct expenses
    const directExpensesTds = periodFilteredBankRows.reduce((s, r) => s + (Number(r.tds_amount) || 0), 0);
    const directExpensesGst = periodFilteredBankRows.reduce((s, r) => s + (Number(r.gst_amount) || 0), 0);

    const totalTds = periodFilteredPurchases.reduce((s, p) => s + (Number(p.TDSAmount) || 0), 0) + directExpensesTds;
    const totalPayables = periodFilteredPurchases.reduce((s, p) => p.paid === 1 ? s : s + (Number(p.Total_Amount) - Number(p.TDSAmount || 0)), 0);

    const totalInwardGst = periodFilteredPurchases.reduce((s, p) => {
      return s + (Number(p.SGSTAmount) || 0) + (Number(p.CGSTAmount) || 0) + (Number(p.IGSTAmount) || 0);
    }, 0) + directExpensesGst;

    let purchasePaidAmount = 0;
    let purchasePendingAmount = 0;
    
    periodFilteredPurchases.forEach(p => {
      const totalAmt = Number(p.Total_Amount) || 0;
      const amtPaid = Number(p.amount_paid) || 0;
      if (p.paid === 1) {
        purchasePaidAmount += totalAmt;
      } else if (p.paid === 2) {
        purchasePaidAmount += amtPaid;
        purchasePendingAmount += (totalAmt - amtPaid);
      } else {
        purchasePendingAmount += totalAmt;
      }
    });

    return {
      total,
      paid,
      unpaid,
      partial,
      paidAmount,
      unpaidAmount,
      partialAmount,
      totalAmount: periodFilteredInvoices.reduce((s, i) => i.cancelled === 1 ? s : s + (i.Total_Amount || 0), 0),
      totalGst,
      totalTds,
      totalTdsReceivable,
      totalPayables,
      totalInwardGst,
      purchasePaidAmount,
      purchasePendingAmount,
      purchasesCount: periodFilteredPurchases.length
    };
  }, [companyFilteredInvoices, purchases, bankRows, companyFilter, matchesPeriodFilters]);

  // Bank Analysis & Survival Burn Calculations
  const bankStats = useMemo(() => {
    const periodFilteredBankRows = bankRows.filter(r => {
      const parsedDate = parseBankDate(r.date_str);
      return matchesPeriodFilters(parsedDate);
    });
    const expenses = periodFilteredBankRows.filter(r => Number(r.debit || 0) > 0);
    if (expenses.length === 0) return { avgDebits: 0, coreBurn: 0, predictedSurvival: 0 };

    const monthsMap = new Map<string, { total: number; recurring: number }>();
    const recurringKeywords = ['salary', 'rent', 'electricity', 'internet', 'aws', 'server', 'cloud', 'emi', 'insurance'];

    expenses.forEach(row => {
      const clean = row.date_str.trim();
      const parts = clean.split(/[-/\s]+/);
      let year = 2026, month = 1;
      if (parts.length === 3) {
        const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
        let monthVal = NaN;
        if (/^\d+$/.test(parts[1])) {
          monthVal = Number(parts[1]);
        } else {
          monthVal = months.indexOf(parts[1].toLowerCase()) + 1;
        }

        if (parts[0].length === 4) {
          year = Number(parts[0]);
          month = !isNaN(monthVal) ? monthVal : Number(parts[1]);
        } else {
          year = Number(parts[2]);
          month = !isNaN(monthVal) ? monthVal : Number(parts[1]);
        }
      }
      const key = `${year}-${String(month).padStart(2, '0')}`;
      
      let cur = monthsMap.get(key);
      if (!cur) {
        cur = { total: 0, recurring: 0 };
        monthsMap.set(key, cur);
      }
      const amt = Number(row.debit || 0);
      cur.total += amt;
      
      const txt = (row.particulars || '').toLowerCase();
      const isRec = recurringKeywords.some(kw => txt.includes(kw));
      if (isRec) cur.recurring += amt;
    });

    const list = Array.from(monthsMap.values());
    if (list.length === 0) return { avgDebits: 0, coreBurn: 0, predictedSurvival: 0 };
    
    const avgDebits = list.reduce((s, m) => s + m.total, 0) / list.length;
    const coreBurn = list.reduce((s, m) => s + m.recurring, 0) / list.length;
    const avgVariable = Math.max(0, avgDebits - coreBurn);

    return {
      avgDebits,
      coreBurn,
      predictedSurvival: coreBurn + (avgVariable * 0.2) // 20% discretionary spending included
    };
  }, [bankRows, matchesPeriodFilters]);

  const filteredInvoices = useMemo(() => {
    const periodFiltered = companyFilteredInvoices.filter(inv => matchesPeriodFilters(inv.Date));
    let filtered = periodFiltered;
    if (statusFilter) {
      filtered = filtered.filter(
        (inv) => (inv.PaymentStatus || "Unpaid") === statusFilter
      );
    }
    if (searchInvoiceNo) {
      filtered = filtered.filter((inv) =>
        inv.InvoiceNo.toLowerCase().includes(searchInvoiceNo.trim().toLowerCase())
      );
    }
    return [...filtered].sort(
      (a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime()
    );
  }, [companyFilteredInvoices, statusFilter, searchInvoiceNo, matchesPeriodFilters]);

  return (
    <div className="w-full min-h-full bg-slate-50/50 py-10 px-4 sm:px-6 lg:px-8">
      {/* Top Banner */}
      <header className="mb-10 bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
            Financial Control Center
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Real-time business performance, billing flows, ledger checks, and cash forecasting.
          </p>
        </div>
        <div className="flex gap-3">
          <a
            href="/InvoiceCreator"
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm px-5 py-2.5 rounded-xl shadow-lg shadow-indigo-600/10 transition"
          >
            <FaPlus /> Create Invoice
          </a>
        </div>
      </header>

      {/* Dynamic Period Filters */}
      <section className="mb-8 bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Period Filters</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Financial Year</label>
            <select
              value={fyFilter}
              onChange={(e) => setFyFilter(e.target.value)}
              className="w-full text-sm border-2 border-slate-100 bg-white px-3 py-2 rounded-xl focus:border-indigo-300 focus:outline-none transition text-slate-700 font-semibold"
            >
              <option value="">All Years</option>
              {availableFys.map(fy => (
                <option key={fy} value={fy}>FY {fy}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Quarter</label>
            <select
              value={quarterFilter}
              onChange={(e) => setQuarterFilter(e.target.value)}
              className="w-full text-sm border-2 border-slate-100 bg-white px-3 py-2 rounded-xl focus:border-indigo-300 focus:outline-none transition text-slate-700 font-semibold"
            >
              <option value="">All Quarters</option>
              <option value="Q1">Q1 (Apr - Jun)</option>
              <option value="Q2">Q2 (Jul - Sep)</option>
              <option value="Q3">Q3 (Oct - Dec)</option>
              <option value="Q4">Q4 (Jan - Mar)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Half Year</label>
            <select
              value={halfYearFilter}
              onChange={(e) => setHalfYearFilter(e.target.value)}
              className="w-full text-sm border-2 border-slate-100 bg-white px-3 py-2 rounded-xl focus:border-indigo-300 focus:outline-none transition text-slate-700 font-semibold"
            >
              <option value="">All Half Years</option>
              <option value="H1">H1 (Apr - Sep)</option>
              <option value="H2">H2 (Oct - Mar)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Month</label>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="w-full text-sm border-2 border-slate-100 bg-white px-3 py-2 rounded-xl focus:border-indigo-300 focus:outline-none transition text-slate-700 font-semibold"
            >
              <option value="">All Months</option>
              <option value="01">January</option>
              <option value="02">February</option>
              <option value="03">March</option>
              <option value="04">April</option>
              <option value="05">May</option>
              <option value="06">June</option>
              <option value="07">July</option>
              <option value="08">August</option>
              <option value="09">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>
        </div>
      </section>

      {/* Main KPI Stats Grid */}
      <div className="mb-8">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Sales Invoicing & Outward tax (Receivables)</h3>
        <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-slate-400 uppercase truncate">Sales Invoiced</div>
            <span className="text-xl font-extrabold text-slate-800 mt-1 truncate">{summary.total} Bills</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate" title={`Val: ${currency(summary.totalAmount)}`}>Val: {currency(summary.totalAmount)}</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-green-500 uppercase truncate">Paid Received</div>
            <span className="text-xl font-extrabold text-green-600 mt-1 truncate">{summary.paid} Bills</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate" title={`Received: ${currency(summary.paidAmount)}`}>Received: {currency(summary.paidAmount)}</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-yellow-500 uppercase truncate">Partially Paid</div>
            <span className="text-xl font-extrabold text-yellow-700 mt-1 truncate">{summary.partial} Bills</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate" title={`Outstanding: ${currency(summary.partialAmount)}`}>Outstanding: {currency(summary.partialAmount)}</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-red-500 uppercase truncate">Unpaid Bills</div>
            <span className="text-xl font-extrabold text-red-700 mt-1 truncate">{summary.unpaid} Bills</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate" title={`Due amount: ${currency(summary.unpaidAmount)}`}>Due amount: {currency(summary.unpaidAmount)}</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-purple-500 uppercase truncate">Est. GST Outward</div>
            <span className="text-xl font-extrabold text-purple-700 mt-1 truncate" title={currency(summary.totalGst)}>{currency(summary.totalGst)}</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate">Sales Tax Liability</span>
          </div>
        </section>

        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Purchases & Inward Tax Credit (Payables)</h3>
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-indigo-500 uppercase truncate">Purchases Recorded</div>
            <span className="text-xl font-extrabold text-indigo-700 mt-1 truncate">{summary.purchasesCount} Bills</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate">Vendor Invoices</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-green-600 uppercase truncate">Paid Purchases</div>
            <span className="text-xl font-extrabold text-green-700 mt-1 truncate" title={currency(summary.purchasePaidAmount)}>{currency(summary.purchasePaidAmount)}</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate">Settled Vendor amount</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-red-500 uppercase truncate">Pending Purchases</div>
            <span className="text-xl font-extrabold text-red-700 mt-1 truncate" title={currency(summary.purchasePendingAmount)}>{currency(summary.purchasePendingAmount)}</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate">Outstanding Payables</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-cyan-600 uppercase truncate">Total Inward GST</div>
            <span className="text-xl font-extrabold text-cyan-700 mt-1 truncate" title={currency(summary.totalInwardGst)}>{currency(summary.totalInwardGst)}</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate">GST Input Tax Credit</span>
          </div>
        </section>

        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Withholding Tax (TDS)</h3>
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-orange-500 uppercase truncate">TDS Deducted (Payables)</div>
            <span className="text-xl font-extrabold text-orange-700 mt-1 truncate" title={currency(summary.totalTds)}>{currency(summary.totalTds)}</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate">Calculated from purchases</span>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col justify-between hover:shadow transition min-w-0">
            <div className="text-[11px] font-bold text-cyan-500 uppercase truncate">TDS Receivable (Sales)</div>
            <span className="text-xl font-extrabold text-cyan-700 mt-1 truncate" title={currency(summary.totalTdsReceivable)}>{currency(summary.totalTdsReceivable)}</span>
            <span className="text-[10px] text-slate-500 mt-2 truncate">Sales TDS Deductible</span>
          </div>
        </section>
      </div>

      {/* Advanced Performance & Prediction Widgets */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        
        {/* Working Capital & Net Cash Position Widget */}
        <div className="bg-white border border-slate-200/70 rounded-3xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-950 flex items-center gap-2 border-b border-slate-100 pb-3">
              <FaCoins className="text-indigo-600" /> Working Capital Position
            </h3>
            
            <div className="grid grid-cols-2 gap-6 mt-5">
              <div>
                <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block">Receivables (A)</span>
                <span className="text-xl font-black text-emerald-600 block mt-1">
                  {currency(summary.unpaidAmount + summary.partialAmount)}
                </span>
                <span className="text-[10px] text-slate-500">Unpaid Sales Invoices</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block">Payables (B)</span>
                <span className="text-xl font-black text-rose-600 block mt-1">
                  {currency(summary.totalPayables)}
                </span>
                <span className="text-[10px] text-slate-500">Pending Purchase Bills</span>
              </div>
            </div>

            {/* Net Position Visual Indicator */}
            <div className="mt-6 bg-slate-50 border border-slate-100 rounded-2xl p-4">
              <div className="flex justify-between items-center mb-1 text-xs">
                <span className="font-bold text-slate-600">Net Flow Position (A - B)</span>
                <span className={`font-black ${((summary.unpaidAmount + summary.partialAmount) - summary.totalPayables) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {currency((summary.unpaidAmount + summary.partialAmount) - summary.totalPayables)}
                </span>
              </div>
              <div className="w-full bg-slate-200 h-2.5 rounded-full overflow-hidden mt-2">
                <div 
                  className={`h-full rounded-full ${((summary.unpaidAmount + summary.partialAmount) - summary.totalPayables) >= 0 ? 'bg-emerald-500' : 'bg-rose-500'}`}
                  style={{ width: `${Math.min(100, Math.max(15, (((summary.unpaidAmount + summary.partialAmount) || 1) / ((summary.unpaidAmount + summary.partialAmount + summary.totalPayables) || 1)) * 100))}%` }}
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-between items-center text-xs text-slate-500 pt-4 border-t border-slate-100 mt-6">
            <span>Dynamic assessment based on unpaid bills</span>
            <a href="/reports" className="text-indigo-600 hover:text-indigo-800 font-bold flex items-center gap-1">
              Outstandings <FaChevronRight size={10} />
            </a>
          </div>
        </div>

        {/* Cash Burn & Survival Prediction Widget */}
        <div className="bg-white border border-slate-200/70 rounded-3xl p-6 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-950 flex items-center gap-2 border-b border-slate-100 pb-3">
              <FaMoneyBillWave className="text-emerald-600" /> Cash Burn & Survival Analysis
            </h3>

            <div className="grid grid-cols-2 gap-6 mt-5">
              <div>
                <span className="text-xs text-slate-400 font-bold uppercase block">Core Fixed Burn Rate</span>
                <span className="text-xl font-black text-slate-800 mt-1 block">
                  {currency(bankStats.coreBurn)}
                </span>
                <span className="text-[10px] text-slate-500">Rent, salary, fixed costs</span>
              </div>
              <div>
                <span className="text-xs text-slate-400 font-bold uppercase block">Monthly Survival Minimum</span>
                <span className="text-xl font-black text-indigo-700 mt-1 block">
                  {currency(bankStats.predictedSurvival)}
                </span>
                <span className="text-[10px] text-slate-500">Fixed + 20% Variable Spend</span>
              </div>
            </div>

            {/* Stability Health Check */}
            <div className="mt-6 flex items-center gap-3 bg-blue-50 border border-blue-100/50 rounded-2xl p-4">
              {bankStats.coreBurn > 0 ? (
                <>
                  <FaCheckCircle className="text-emerald-600 shrink-0" size={20} />
                  <div className="text-xs text-blue-900 leading-normal">
                    Auto-detected monthly overhead burn is stable. You need a minimum of <span className="font-bold">{currency(bankStats.predictedSurvival)}</span> cash reserves to survive this month.
                  </div>
                </>
              ) : (
                <>
                  <FaExclamationTriangle className="text-yellow-600 shrink-0" size={20} />
                  <div className="text-xs text-blue-900 leading-normal">
                    No bank statement data uploaded yet. Categorize statement rows to evaluate minimum cash burn.
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center text-xs text-slate-500 pt-4 border-t border-slate-100 mt-6">
            <span>Calculated from bank statement debits</span>
            <a href="/reports" className="text-indigo-600 hover:text-indigo-800 font-bold flex items-center gap-1">
              Survival Forecast <FaChevronRight size={10} />
            </a>
          </div>
        </div>
      </section>

      {/* Revamped Quick Portal Dashboard Grid */}
      <section className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm mb-10">
        <h3 className="text-lg font-bold text-slate-900 mb-5 border-b border-slate-100 pb-3 flex items-center gap-2">
          <FaSlidersH className="text-indigo-600" /> Quick Operations Portal
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-5 text-center">
          
          <a href="/InvoiceCreator" className="flex flex-col items-center gap-3 p-4 bg-slate-50 hover:bg-indigo-50 border border-slate-100 rounded-2xl transition hover:shadow-md group">
            <div className="p-3 bg-indigo-500 text-white rounded-xl shadow group-hover:scale-105 transition duration-200">
              <FaFileInvoice size={18} />
            </div>
            <span className="text-xs font-bold text-slate-700">Create Invoice</span>
          </a>

          <a href="/purchase-invoice-creator" className="flex flex-col items-center gap-3 p-4 bg-slate-50 hover:bg-blue-50 border border-slate-100 rounded-2xl transition hover:shadow-md group">
            <div className="p-3 bg-blue-500 text-white rounded-xl shadow group-hover:scale-105 transition duration-200">
              <FaReceipt size={18} />
            </div>
            <span className="text-xs font-bold text-slate-700">Record Purchase</span>
          </a>

          <a href="/statement-upload" className="flex flex-col items-center gap-3 p-4 bg-slate-50 hover:bg-emerald-50 border border-slate-100 rounded-2xl transition hover:shadow-md group">
            <div className="p-3 bg-emerald-500 text-white rounded-xl shadow group-hover:scale-105 transition duration-200">
              <FaUpload size={18} />
            </div>
            <span className="text-xs font-bold text-slate-700">Upload Statement</span>
          </a>

          <a href="/party-ledger" className="flex flex-col items-center gap-3 p-4 bg-slate-50 hover:bg-purple-50 border border-slate-100 rounded-2xl transition hover:shadow-md group">
            <div className="p-3 bg-purple-500 text-white rounded-xl shadow group-hover:scale-105 transition duration-200">
              <FaBookOpen size={18} />
            </div>
            <span className="text-xs font-bold text-slate-700">Party Ledger</span>
          </a>

          <a href="/reports" className="flex flex-col items-center gap-3 p-4 bg-slate-50 hover:bg-rose-50 border border-slate-100 rounded-2xl transition hover:shadow-md group">
            <div className="p-3 bg-rose-500 text-white rounded-xl shadow group-hover:scale-105 transition duration-200">
              <FaChartBar size={18} />
            </div>
            <span className="text-xs font-bold text-slate-700">Reports Panel</span>
          </a>

          <a href="/customer" className="flex flex-col items-center gap-3 p-4 bg-slate-50 hover:bg-cyan-50 border border-slate-100 rounded-2xl transition hover:shadow-md group">
            <div className="p-3 bg-cyan-500 text-white rounded-xl shadow group-hover:scale-105 transition duration-200">
              <FaWallet size={18} />
            </div>
            <span className="text-xs font-bold text-slate-700">Customer List</span>
          </a>

          <a href="/recurring-invoices" className="flex flex-col items-center gap-3 p-4 bg-slate-50 hover:bg-amber-50 border border-slate-100 rounded-2xl transition hover:shadow-md group">
            <div className="p-3 bg-amber-500 text-white rounded-xl shadow group-hover:scale-105 transition duration-200">
              <FaPercentage size={18} />
            </div>
            <span className="text-xs font-bold text-slate-700">Recurring Bills</span>
          </a>

        </div>
      </section>

      {/* Invoice Records Listing & Filters */}
      <section className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-slate-900 mb-5 border-b border-slate-100 pb-3 flex items-center justify-between">
          <span>Billing Registry</span>
          <span className="text-xs text-indigo-600 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full font-bold">
            {filteredInvoices.length} Invoices
          </span>
        </h3>
        
        {/* Filter Inputs */}
        <div className="flex flex-wrap gap-4 items-end mb-6">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Search Invoice No.</label>
            <input
              type="text"
              placeholder="e.g. INV-2026-001..."
              className="w-full text-sm border-2 border-slate-100 bg-white px-3 py-2 rounded-xl focus:border-indigo-300 focus:outline-none transition text-slate-700 shadow-inner"
              value={searchInvoiceNo}
              onChange={(e) => setSearchInvoiceNo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Company Issuer</label>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="w-48 text-sm border-2 border-slate-100 bg-white px-3 py-2 rounded-xl focus:border-indigo-300 focus:outline-none transition text-slate-700"
            >
              <option value="">All Issuers</option>
              {companies.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.company_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Payment Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-44 text-sm border-2 border-slate-100 bg-white px-3 py-2 rounded-xl focus:border-indigo-300 focus:outline-none transition text-slate-700"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="overflow-x-auto border border-slate-100 rounded-2xl shadow-inner bg-white">
          <table className="min-w-full divide-y divide-slate-150">
            <thead className="bg-slate-50/50 text-left text-[11px] font-extrabold text-slate-400 uppercase tracking-wider select-none">
              <tr>
                <th className="py-4 px-3">#</th>
                <th className="py-4 px-3">Invoice No</th>
                <th className="py-4 px-3">Date</th>
                <th className="py-4 px-3">Company Issuer</th>
                <th className="py-4 px-3">Customer Recipient</th>
                <th className="py-4 px-3 text-right">Amount</th>
                <th className="py-4 px-3">Payments</th>
                <th className="py-4 px-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-700 font-medium">
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-400">
                    Loading Billing registry records...
                  </td>
                </tr>
              ) : filteredInvoices && filteredInvoices.length > 0 ? (
                filteredInvoices.map((inv, idx) => (
                  <tr
                    key={inv._id || inv.InvoiceNo || idx}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="py-3 px-3 text-slate-400 font-semibold">{idx + 1}</td>
                    <td className="py-3 px-3 font-bold text-indigo-750">
                      {inv.InvoiceNo}
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">{formatDate(inv.Date)}</td>
                    <td className="py-3 px-3 max-w-[150px] truncate">
                      {getCompanyName(companies, inv.Company_id)}
                    </td>
                    <td className="py-3 px-3 max-w-[150px] truncate">
                      {getCustomerName(customers, inv.Customer_id)}
                    </td>
                    <td className="py-3 px-3 text-right font-extrabold text-slate-900">{currency(inv.Total_Amount)}</td>
                    <td className="py-3 px-3">{getPaidPartial(inv)}</td>
                    <td className="py-3 px-3 text-center">{getStatusBadge(inv.paid)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-slate-400 font-semibold italic">
                    No matching billing invoices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;